import { Box, Group, Text, UnstyledButton } from '@mantine/core';
import { useState, type FC, type ReactNode } from 'react';
import { Link } from 'react-router';
import Omnibar from '../omnibar';
import UserMenu from '../../components/NavBar/UserMenu';
import useApp from '../../providers/App/useApp';
import TapeUploadModal from '../tapes/TapeUploadModal';
import { AskDialog } from './LookthroughPageHeader';
import classes from './lookthrough.module.css';
import { isStaff, useLookthroughContext } from './useLookthroughContext';

/** Phil's layered-aperture mark (transcripts/ground_truth_frontend/lookthrough-logo.svg). */
const Mark: FC = () => (
    <svg className={classes.mark} viewBox="0 0 64 64" aria-hidden="true">
        <defs>
            <linearGradient id="lt-a" x1="0" x2="1">
                <stop offset="0" stopColor="#9ec4e4" />
                <stop offset="1" stopColor="#d9e6f2" />
            </linearGradient>
            <linearGradient id="lt-b" x1="0" x2="1">
                <stop offset="0" stopColor="#4f88b6" />
                <stop offset="1" stopColor="#8db5d8" />
            </linearGradient>
        </defs>
        <path d="M12 15 32 4l20 11-20 12z" fill="url(#lt-a)" opacity=".9" />
        <path d="M12 27 32 16l20 11-20 12z" fill="url(#lt-b)" opacity=".92" />
        <path d="M12 39 32 28l20 11-20 12z" fill="#edf4fa" opacity=".95" />
        <path d="M12 51 32 40l20 11-20 9z" fill="#7ba6ca" opacity=".9" />
    </svg>
);

/**
 * Phil's frame for LOOKTHROUGH pages (spec 7.3 G3, D.2): a fixed left sidebar listing the pages in
 * page-file order, and his top bar with Lightdash's own search and user menu. ProjectLayout renders
 * it on every project route with the same element shape; off a LOOKTHROUGH URL the boxes are
 * display: contents and the bars are not drawn, so moving between pages never remounts the page.
 */
const LookthroughShell: FC<{ children: ReactNode; active: boolean }> = ({ children, active }) => {
    const { projectUuid, page, pages, context } = useLookthroughContext();
    const { user } = useApp();
    const staff = isStaff(user.data?.role);
    const [help, setHelp] = useState(false);
    const [tapes, setTapes] = useState(false);
    return (
        // data-client: no tile links, menus or later-release cards for clients (D.1, D.3, E18);
        // data-filters: the page keeps Lightdash's Add filter row (E2)
        <Box className={active ? classes.app : classes.off} data-client={(active && !staff) || undefined}
            data-filters={page?.filters || undefined}>
            {active && (
                <Box component="aside" className={classes.sidebar} data-testid="lookthrough-sidebar">
                    <Group className={classes.brand} gap={11} wrap="nowrap">
                        <Mark />
                        <Box>
                            <Text className={classes.brandName}>LOOKTHROUGH™</Text>
                            <Text className={classes.brandSub}>by QueensGiant</Text>
                        </Box>
                    </Group>
                    <Box component="nav" className={classes.nav}>
                        {pages.map((p) => (
                            <UnstyledButton
                                key={p.slug}
                                component={Link}
                                to={`/projects/${projectUuid}/dashboards/${p.slug}/view${context}`}
                                className={classes.navItem}
                                data-active={p.slug === page?.slug || undefined}
                            >
                                <span className={classes.ico}>{p.icon}</span>
                                <span>{p.name}</span>
                            </UnstyledButton>
                        ))}
                    </Box>
                    <Box className={classes.sidebarFoot}>
                        {staff && projectUuid && (   // QueensGiant imports tapes in Release 1 (D.1, E20)
                            <UnstyledButton className={classes.navItem} onClick={() => setTapes(true)}>
                                <span className={classes.ico}>⇪</span>
                                <span>Tapes</span>
                            </UnstyledButton>
                        )}
                        <UnstyledButton className={classes.navItem} onClick={() => setHelp(true)}>
                            Help &amp; Support
                        </UnstyledButton>
                    </Box>
                    <AskDialog opened={help} onClose={() => setHelp(false)} subject={`LOOKTHROUGH · ${page?.name ?? ''} · help`}
                        body={'Hello QueensGiant,\n\nI need help with: \n\n'} />
                    {tapes && projectUuid && <TapeUploadModal projectUuid={projectUuid} onClose={() => setTapes(false)} />}
                </Box>
            )}
            <Box className={active ? classes.main : classes.off}>
                {/* id: Lightdash's menus open into #navbar-header (NavBarPortalContext) */}
                {active && (
                    <Group id="navbar-header" component="header" className={classes.topbar} wrap="nowrap">
                        <Box className={classes.search}>
                            {projectUuid && <Omnibar projectUuid={projectUuid} />}
                        </Box>
                        <Group className={classes.user} wrap="nowrap">
                            <UserMenu />
                            <Box>
                                <Text className={classes.userName}>
                                    {user.data?.firstName} {user.data?.lastName}
                                </Text>
                                <Text className={classes.userOrg}>{user.data?.organizationName}</Text>
                            </Box>
                        </Group>
                    </Group>
                )}
                <Box className={active ? classes.content : classes.off}>{children}</Box>
            </Box>
        </Box>
    );
};

export default LookthroughShell;
